import type { WabunkaReservationQuestionAnswerViewModel } from '@/models/wabunka/wabunkaReservationQuestionAnswer';
import { isReservationQuestionAnswered } from '@/wabunka-src/common/features/Reservation/core/requiredFields/reservationQuestions';

export interface ReservationQuestionAnsweredEvent {
  questionId: string;
  /** 追跡対象のうち、これまでに 1 度でも埋まった数。回答を消しても減らない（D-033） */
  answeredCount: number;
  requiredCount: number;
}

export interface ReservationQuestionGaEventState {
  /** 記録がどの体験のものか。予約質問は体験マスタに紐づくため、体験が変われば記録を捨てる */
  experienceId: string;
  /** 予約フォーム着地時点で未回答だった必須質問。既定で埋まる Radio はゲストの関門ではないので分母にも入れない（D-035） */
  trackedQuestionIds: string[];
  sentQuestionIds: string[];
  hasSentComplete: boolean;
}

export interface ReservationQuestionGaEventPlan {
  answeredEvents: ReservationQuestionAnsweredEvent[];
  shouldSendComplete: boolean;
  requiredCount: number;
  nextState: ReservationQuestionGaEventState;
}

export const createDefaultReservationQuestionGaEventState = (): ReservationQuestionGaEventState => ({
  experienceId: '',
  trackedQuestionIds: [],
  sentQuestionIds: [],
  hasSentComplete: false,
});

/**
 * 予約質問の計測イベントを、いまの回答状態と送信済みの記録から決める。
 *
 * 分母は**予約フォーム着地時点で未回答だった必須質問**で、体験が変われば取り直す（`D-035`）。
 * 回答本文は PII のため plan に載せない（`D-033`）。旧動線・新動線のどちらから呼んでも同じ結果になる。
 */
export const planReservationQuestionGaEvents = (
  experienceId: string,
  questionAnswers: WabunkaReservationQuestionAnswerViewModel[],
  state: ReservationQuestionGaEventState,
): ReservationQuestionGaEventPlan => {
  const requiredQuestions = questionAnswers.filter((questionAnswer) => questionAnswer.isRequired);
  const isNewExperience = state.experienceId !== experienceId;
  const trackedQuestionIds = isNewExperience
    ? requiredQuestions.filter((questionAnswer) => !isReservationQuestionAnswered(questionAnswer)).map((questionAnswer) => questionAnswer.id)
    : state.trackedQuestionIds;
  const hasSentComplete = isNewExperience ? false : state.hasSentComplete;
  const requiredCount = trackedQuestionIds.length;

  const nextSentQuestionIds = isNewExperience ? [] : [...state.sentQuestionIds];
  const answeredEvents = requiredQuestions
    .filter((questionAnswer) => trackedQuestionIds.includes(questionAnswer.id) && isReservationQuestionAnswered(questionAnswer) && !nextSentQuestionIds.includes(questionAnswer.id))
    .map((questionAnswer) => {
      nextSentQuestionIds.push(questionAnswer.id);
      return { questionId: questionAnswer.id, answeredCount: nextSentQuestionIds.length, requiredCount };
    });

  const shouldSendComplete = requiredCount > 0 && nextSentQuestionIds.length === requiredCount && !hasSentComplete;
  return {
    answeredEvents,
    shouldSendComplete,
    requiredCount,
    nextState: { experienceId, trackedQuestionIds, sentQuestionIds: nextSentQuestionIds, hasSentComplete: hasSentComplete || shouldSendComplete },
  };
};
